import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Area, AreaChart,
  BarChart, Bar,
} from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, PieChart as PieIcon, BarChart3, Loader2, Trophy } from "lucide-react";

// Distinct, vibrant palette — one visually different colour per region.
// Ordered so adjacent slices in the pie never look similar.
const PALETTE = [
  "#0A84FF", // blu
  "#34C759", // verde
  "#FF9500", // arancio
  "#AF52DE", // viola
  "#FF375F", // rosa/rosso
  "#5AC8FA", // ciano
  "#FFCC00", // giallo
  "#12466F", // blu profondo
  "#1F7A3A", // verde scuro
  "#FB923C", // arancio caldo
  "#C084FC", // lavanda
  "#0891B2", // teal
  "#DC2626", // rosso
  "#64748B", // grigio
  "#059669", // smeraldo
  "#7C3AED", // indaco
  "#F97316", // pumpkin
  "#0EA5E9", // sky
  "#EF4444", // coral
  "#22A34C", // erba
];
const colorFor = (i) => PALETTE[i % PALETTE.length];

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtInt = (n) => new Intl.NumberFormat("it-IT").format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
};

// ---- Custom tooltips -------------------------------------------------------
const PieTip = ({ active, payload, total }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const pct = total > 0 ? ((p.count / total) * 100).toFixed(1) : "0.0";
  return (
    <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl">
      <div className="font-semibold uppercase tracking-wide">{p.region}</div>
      <div className="text-slate-200 mt-0.5">{fmtInt(p.count)} immobili · <span className="text-[#5AC8FA] font-mono-num">{pct}%</span></div>
      <div className="text-slate-400 text-[10px] mt-0.5">{fmtEuro(p.total_prezzo)} totale</div>
    </div>
  );
};

const LineTip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const cum = payload.find((p) => p.dataKey === "cumulative")?.value;
  const day = payload.find((p) => p.dataKey === "count")?.value;
  return (
    <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl">
      <div className="font-semibold">{new Date(label).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</div>
      <div className="mt-1 flex items-center gap-4">
        <div><span className="text-[#5AC8FA]">Totale cumulato:</span> <span className="font-mono-num font-bold">{fmtInt(cum)}</span></div>
        {day !== undefined && <div><span className="text-[#34C759]">Nuovi nel giorno:</span> <span className="font-mono-num font-bold">{fmtInt(day)}</span></div>}
      </div>
    </div>
  );
};

const BarTip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl">
      <div className="font-semibold uppercase tracking-wide">{p.region}</div>
      <div className="text-slate-200 mt-0.5">Prezzo medio: <span className="font-mono-num font-bold text-[#5AC8FA]">{fmtEuro(p.avg_prezzo)}</span></div>
      <div className="text-slate-400 text-[10px] mt-0.5">{fmtInt(p.count)} immobili nel campione</div>
    </div>
  );
};

// ---- Container card --------------------------------------------------------
const ChartCard = ({ icon: Icon, title, subtitle, children, testId }) => (
  <div className="ios-card p-5 relative overflow-hidden bg-gradient-to-br from-white via-white to-slate-50" data-testid={testId}>
    <div className="flex items-start justify-between mb-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
          <Icon className="w-3.5 h-3.5 text-[#007AFF]" strokeWidth={1.75} />
          {title}
        </div>
        <div className="text-sm text-slate-600 mt-1">{subtitle}</div>
      </div>
    </div>
    <div className="h-64" style={{ width: "100%" }}>{children}</div>
  </div>
);

// ---- Main component --------------------------------------------------------
const AnalyticsCharts = ({ refreshKey = 0, titleHidden = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const goToProperty = (p) => {
    if (!p?.board_id || !p?.id) return;
    navigate(`/boards/${p.board_id}?focus=${p.id}`);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/analytics/overview");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error("Errore caricamento statistiche");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const byRegion = useMemo(() => data?.by_region || [], [data]);
  const total = data?.total_count || 0;
  const timeline = data?.timeline || [];
  const topScost = data?.top_scostamenti || [];
  // Bar chart: keep top 8 regions by count for readability.
  const topForPrice = useMemo(() => {
    const arr = [...byRegion].filter((r) => r.count > 0);
    arr.sort((a, b) => b.count - a.count);
    return arr.slice(0, 8);
  }, [byRegion]);

  // Empty-state message when the user has no properties yet
  if (!loading && total === 0) {
    return (
      <div className="ios-card p-8 bg-gradient-to-br from-[#E5F0FF] via-white to-[#E8F7EE]" data-testid="analytics-empty">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-white/70 border border-slate-200 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-[#007AFF]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-tight">Nessuna statistica ancora disponibile</div>
            <p className="text-sm text-slate-600 mt-1">Aggiungi il primo immobile in una scheda: i grafici qui sopra si popoleranno automaticamente.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="analytics-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="ios-card p-5 h-80 flex items-center justify-center bg-gradient-to-br from-white to-slate-50">
            <Loader2 className="w-6 h-6 animate-spin text-[#007AFF]" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <section
      className="mb-8 relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-slate-50 to-white p-6 md:p-8 border border-slate-200 shadow-sm"
      data-testid="analytics-section"
    >
      {/* Subtle deco: soft blue glow in top-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-40"
        style={{ background: "radial-gradient(closest-side, rgba(10,132,255,0.20), transparent)" }}
      />
      <div className="relative flex items-end justify-between mb-6">
        {titleHidden ? (
          <div />
        ) : (
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Overview</div>
            <h2 className="font-display font-bold text-xl leading-tight mt-1 text-slate-900">Statistiche del tuo portafoglio</h2>
          </div>
        )}
        <div className="text-[11px] text-slate-500 font-mono-num">
          {fmtInt(total)} immobili · {data?.regions_count || 0} regioni
        </div>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {/* --- PIE: distribution by region -------------------------------- */}
        <ChartCard
          icon={PieIcon}
          title="Distribuzione per regione"
          subtitle={`${byRegion.length} regioni in archivio`}
          testId="chart-pie-region"
        >
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={byRegion}
                dataKey="count"
                nameKey="region"
                cx="50%" cy="50%"
                innerRadius={45}
                outerRadius={80}
                paddingAngle={2}
                stroke="#fff"
                strokeWidth={2}
              >
                {byRegion.map((_, i) => <Cell key={i} fill={colorFor(i)} />)}
              </Pie>
              <Tooltip content={<PieTip total={total} />} />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
                formatter={(v) => <span className="text-slate-700">{v}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* --- LINE: cumulative timeline of insertions -------------------- */}
        <ChartCard
          icon={TrendingUp}
          title="Andamento inserimenti"
          subtitle="Totale cumulato nel tempo"
          testId="chart-line-timeline"
        >
          <ResponsiveContainer>
            <AreaChart data={timeline} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cumFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#007AFF" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#007AFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#eef2f7" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDate}
                tick={{ fontSize: 10, fill: "#64748b" }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                width={30}
              />
              <Tooltip content={<LineTip />} />
              <Area
                type="monotone"
                dataKey="cumulative"
                stroke="#007AFF"
                strokeWidth={2.5}
                fill="url(#cumFill)"
                dot={{ r: 2, fill: "#007AFF" }}
                activeDot={{ r: 5, fill: "#12466F", stroke: "#fff", strokeWidth: 2 }}
                name="Totale immobili"
                isAnimationActive
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#34C759"
                strokeWidth={0}
                dot={false}
                activeDot={false}
                name="Nuovi nel giorno"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* --- LINE: €/m² trend per region with glow + projection ---------- */}
        <ChartCard
          icon={BarChart3}
          title="Trend €/m² per regione"
          subtitle="Media mensile + proiezione a 3 mesi"
          testId="chart-region-timeline"
        >
          {(() => {
            const rt = data?.region_timeline || [];
            if (!rt.length) {
              return <div className="h-full flex items-center justify-center text-slate-400 text-xs">Serve almeno un immobile con €/m² e data valida.</div>;
            }
            // Keep the top-8 regions by property count so the chart stays readable.
            const keepSet = new Set(topForPrice.map((r) => r.region));
            const regions = rt.filter((r) => keepSet.has(r.region)).slice(0, 8);

            // Collect all months across selected regions (real observations),
            // sort ascending, then extend with 3 forecast months for the projection.
            const monthSet = new Set();
            regions.forEach((r) => r.points.forEach((p) => monthSet.add(p.month)));
            const monthsAsc = [...monthSet].sort();
            if (monthsAsc.length === 0) {
              return <div className="h-full flex items-center justify-center text-slate-400 text-xs">Dati insufficienti.</div>;
            }
            const nextMonths = [];
            const [ly, lm] = monthsAsc[monthsAsc.length - 1].split("-").map(Number);
            let y = ly, m = lm;
            for (let i = 0; i < 3; i++) {
              m += 1; if (m > 12) { m = 1; y += 1; }
              nextMonths.push(`${y}-${String(m).padStart(2, "0")}`);
            }
            const allMonths = [...monthsAsc, ...nextMonths];

            // Fit a simple linear regression on each region (x = index into monthsAsc)
            // and extrapolate the forecast keys `${region}__proj` for the last real
            // point + the 3 forecast months (so the dashed segment connects).
            const chartData = allMonths.map((month) => ({ month }));
            regions.forEach((r) => {
              const map = new Map(r.points.map((p) => [p.month, p.eur_mq]));
              // real series
              chartData.forEach((row) => { if (map.has(row.month)) row[r.region] = map.get(row.month); });
              // regression
              const xs = [], ys = [];
              r.points.forEach((p) => {
                const idx = monthsAsc.indexOf(p.month);
                if (idx >= 0) { xs.push(idx); ys.push(p.eur_mq); }
              });
              if (xs.length < 2) return;
              const n = xs.length;
              const sumX = xs.reduce((a, b) => a + b, 0);
              const sumY = ys.reduce((a, b) => a + b, 0);
              const sumXY = xs.reduce((a, x, i) => a + x * ys[i], 0);
              const sumXX = xs.reduce((a, x) => a + x * x, 0);
              const denom = n * sumXX - sumX * sumX;
              if (!denom) return;
              const slope = (n * sumXY - sumX * sumY) / denom;
              const intercept = (sumY - slope * sumX) / n;
              const lastIdx = monthsAsc.length - 1;
              // Attach the projection ANCHORED at the last real point so the
              // dashed line joins seamlessly.
              const projKey = `${r.region}__proj`;
              chartData[lastIdx][projKey] = map.get(monthsAsc[lastIdx]);
              nextMonths.forEach((mm, i) => {
                const x = lastIdx + 1 + i;
                chartData[monthsAsc.length + i][projKey] = Math.max(0, Math.round(intercept + slope * x));
              });
            });

            return (
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                  <defs>
                    {/* Per-region glow filter — big blur behind the crisp
                        overlay line to create the "neon" halo effect. */}
                    {regions.map((r, i) => (
                      <filter key={i} id={`glow-${i}`} x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="3.5" result="blur" />
                        <feMerge>
                          <feMergeNode in="blur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    ))}
                  </defs>
                  <CartesianGrid stroke="#eef2f7" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 9, fill: "#64748b" }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickLine={false}
                    tickFormatter={(m) => {
                      const [yy, mm] = String(m).split("-");
                      return `${mm}/${String(yy).slice(2)}`;
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                    label={{ value: "€/m²", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" } }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const [yy, mm] = String(label).split("-");
                      // Filter out `__proj` duplicates when the real value exists.
                      const seen = new Set();
                      const rows = [];
                      payload.forEach((p) => {
                        const base = p.dataKey.replace(/__proj$/, "");
                        if (seen.has(base)) return;
                        seen.add(base);
                        const isProj = p.dataKey.endsWith("__proj");
                        rows.push({ region: base, value: p.value, projected: isProj, color: p.color });
                      });
                      return (
                        <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl min-w-[160px]">
                          <div className="font-semibold uppercase tracking-wide mb-1">{mm}/{yy}</div>
                          {rows.map((r, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                              <span className="text-slate-200 flex-1 truncate">{r.region}</span>
                              <span className="font-mono-num font-bold" style={{ color: r.color }}>
                                {fmtEuro(r.value)}{r.projected ? " ·" : ""}
                              </span>
                              {r.projected && <span className="text-[9px] uppercase tracking-widest text-slate-400">proj.</span>}
                            </div>
                          ))}
                        </div>
                      );
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconType="plainline"
                    wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
                    formatter={(v) => (v || "").endsWith("__proj") ? null : <span className="text-slate-700">{v}</span>}
                  />
                  {regions.map((r, i) => {
                    const c = colorFor(i);
                    return (
                      <React.Fragment key={r.region}>
                        {/* Glow underlay — same colour, thick, blurred */}
                        <Line
                          type="monotone" dataKey={r.region} name={r.region}
                          stroke={c} strokeWidth={7} strokeOpacity={0.45}
                          dot={false} activeDot={false}
                          filter={`url(#glow-${i})`}
                          isAnimationActive
                          animationDuration={900}
                          animationBegin={i * 60}
                          legendType="none"
                          connectNulls
                        />
                        {/* Crisp overlay */}
                        <Line
                          type="monotone" dataKey={r.region} name={r.region}
                          stroke={c} strokeWidth={2.25}
                          dot={{ r: 2.5, fill: c, stroke: "#fff", strokeWidth: 1 }}
                          activeDot={{ r: 5, fill: c, stroke: "#fff", strokeWidth: 2 }}
                          isAnimationActive
                          animationDuration={900}
                          animationBegin={i * 60}
                          connectNulls
                        />
                        {/* Dashed projection (linear-regression forecast) */}
                        <Line
                          type="monotone" dataKey={`${r.region}__proj`} name={`${r.region}__proj`}
                          stroke={c} strokeWidth={1.75}
                          strokeDasharray="5 4"
                          dot={{ r: 2, fill: "#fff", stroke: c, strokeWidth: 1.5 }}
                          activeDot={false}
                          isAnimationActive={false}
                          legendType="none"
                          connectNulls
                        />
                      </React.Fragment>
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            );
          })()}
        </ChartCard>

        {/* --- LEADERBOARD: top scostamento (best deals) ------------------ */}
        <ChartCard
          icon={Trophy}
          title="Migliori scostamenti"
          subtitle="Top 10 sotto la media €/m²"
          testId="chart-top-scostamento"
        >
          {topScost.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-xs">
              Nessun immobile con scostamento calcolabile
            </div>
          ) : (
            <div className="h-full overflow-y-auto pr-1 -mr-1" data-testid="top-scostamento-list">
              <ol className="space-y-1.5">
                {topScost.map((p, i) => {
                  const pct = (p.scostamento * 100);
                  const isBest = i === 0;
                  const color = pct <= -30 ? "#FF3B30" : pct <= -20 ? "#B27F00" : pct <= -10 ? "#1F7A3A" : pct < 0 ? "#00A6B7" : "#0A84FF";
                  return (
                    <li
                      key={p.id}
                      className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${isBest ? "bg-gradient-to-r from-[#FEF3C7] to-transparent hover:from-[#FDE68A]" : "hover:bg-[#007AFF]/8"}`}
                      onClick={() => goToProperty(p)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToProperty(p); } }}
                      title={`Apri "${p.indirizzo}" nella scheda ${p.board_name}`}
                      data-testid={`top-scost-${i}`}
                    >
                      <span
                        className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${isBest ? "bg-[#F59E0B] text-white" : "bg-slate-100 text-slate-600"}`}
                      >
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-semibold text-slate-800 truncate uppercase" title={p.indirizzo}>
                          {p.indirizzo || "—"}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {p.board_name} · {fmtInt(p.mq)} m² · {fmtEuro(p.prezzo)}
                        </div>
                      </div>
                      <span
                        className="shrink-0 text-[10px] font-mono-num font-bold rounded-full px-2 py-0.5 border"
                        style={{ color, borderColor: color, background: `${color}15` }}
                      >
                        {pct.toFixed(1)}%
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </ChartCard>
      </div>
    </section>
  );
};

export default AnalyticsCharts;
